import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/motion-primitives";
import { SectionHead, Field, Input, Select, Textarea, SubmitButton, usePageMeta } from "@/components/shared";
import { submitLead, formatApiError } from "@/lib/api";

export default function GuestSupport() {
  usePageMeta("Guest Support", "Support for guests currently staying at properties managed by The Bermuda Co-Host.");
  const [form, setForm] = useState({ name: "", email: "", phone: "", booking_reference: "", issue_category: "", urgency: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead("guest_support", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <section className="mx-auto max-w-[820px] px-6 pb-28 pt-40 lg:px-10">
        <SectionHead
          overline="Guest Support"
          title={<>Staying at one of<br /><span className="italic text-bermuda-dark">our properties?</span></>}
          copy="This area is for guests currently staying at a property managed by The Bermuda Co-Host. For anything urgent during your stay, call us directly — otherwise send the form below and our local team will respond promptly."
        />
        <Reveal delay={0.1}>
          <div className="mt-8 flex items-start gap-4 bg-sand-light p-5">
            <ShieldCheck size={17} className="mt-0.5 shrink-0 text-ocean" />
            <p className="text-xs leading-relaxed text-navy/60">
              Your privacy matters. This form never displays property details, access information or other guests'
              data — it simply connects you to our team. Property information is only ever shared with verified guests
              and owners through secure channels.{" "}
              <a href="/cleaning#guest-cleaning" data-testid="guest-support-cleaning-link" className="text-bermuda-dark underline underline-offset-4 hover:text-navy">
                Looking for a mid-stay clean instead? Book guest cleaning →
              </a>
              <br />
              <a href="/loyalty" data-testid="guest-support-loyalty-link" className="mt-2 inline-block text-bermuda-dark underline underline-offset-4 hover:text-navy">
                Stayed with us before? The Co-Host Circle rewards returning guests →
              </a>
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-10 border border-navy/10 bg-white p-8 shadow-xl shadow-navy/5 lg:p-12">
            {done ? (
              <div className="py-8 text-center" data-testid="guest-support-success">
                <CheckCircle2 size={40} strokeWidth={1.2} className="mx-auto text-bermuda-dark" />
                <h3 className="mt-6 font-serif text-3xl text-navy">We're on it.</h3>
                <p className="mx-auto mt-4 max-w-sm text-sm text-navy/60">Your request has been sent to our local team. If your issue is urgent, please also call +1 (441) 519-1794.</p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="grid gap-5 sm:grid-cols-2" data-testid="guest-support-form">
                <Field label="Your Name" required><Input data-testid="guest-name" required value={form.name} onChange={set("name")} /></Field>
                <Field label="Email" required><Input data-testid="guest-email" required type="email" value={form.email} onChange={set("email")} /></Field>
                <Field label="Phone" required><Input data-testid="guest-phone" required type="tel" value={form.phone} onChange={set("phone")} /></Field>
                <Field label="Booking Reference / Property Name" required><Input data-testid="guest-booking-ref" required value={form.booking_reference} onChange={set("booking_reference")} placeholder="From your confirmation" /></Field>
                <Field label="Issue Category" required>
                  <Select data-testid="guest-issue-category" required value={form.issue_category} onChange={set("issue_category")}>
                    <option value="">Select…</option>
                    <option>Check-in / access</option><option>Appliance or equipment</option><option>Plumbing</option>
                    <option>Electricity / AC</option><option>Internet / Wi-Fi</option><option>Cleanliness</option><option>Something else</option>
                  </Select>
                </Field>
                <Field label="Urgency" required>
                  <Select data-testid="guest-urgency" required value={form.urgency} onChange={set("urgency")}>
                    <option value="">Select…</option><option>Urgent — affects my stay now</option><option>Soon — within 24 hours</option><option>When convenient</option>
                  </Select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Describe the Issue" required><Textarea data-testid="guest-message" required value={form.message} onChange={set("message")} /></Field>
                </div>
                <div className="sm:col-span-2">
                  <SubmitButton loading={loading} testid="guest-support-submit">Request Assistance</SubmitButton>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </section>
    </main>
  );
}
